import type {
  JWebWindow,
  AccessToken,
  AuthPlugin,
  GetTokenAuthPluginError,
  GetTokenOptions,
  IdentityProviderConfigurationsResult,
  ProviderType
} from '../types';
import { jWebStates } from '../Initializer';
import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';
import InitializationTokenCache from '../cache/InitializationTokenCache';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { getDeviceVersion, isHpSmartAppVersion } from './FacadeUtils';
import { timeout, withTimeout } from '../../../utils/withTimeout';
import { ClientOSError } from '../../../infra/errors/ClientOSError';
import { ClientOSErrorCodes } from '../../../infra/errors/ErrorCodes';

/**
 * AuthFacade provides a simplified interface for Auth plugin operations.
 */
export class AuthPluginFacade {
  public static IS_COMMONS_IN_FALLBACK_STATE = false;

  /**
   * Gets the Auth plugin instance
   * @private
   * @returns The Auth plugin instance or null if not available
   */
  public static async getAuthPlugin(): Promise<AuthPlugin | undefined> {
    await this.isAuthReady();
    const windowAuthPlugin: AuthPlugin = (window as JWebWindow)?.JWeb?.Plugins
      ?.Auth;
    if (windowAuthPlugin === undefined) {
      console?.error('Unable to obtain AuthPlugin (missing some JWeb plugin)');
    }
    return windowAuthPlugin;
  }

  /**
   * Checks if the Auth plugin is ready for use
   * @private
   * @returns Promise that resolves to true if Auth plugin is ready
   */
  private static async isAuthReady(): Promise<boolean> {
    await jWebStates.JWebLibrary;
    await jWebStates.defaultPluginsRegistered;
    await jWebStates.plugins.Auth;

    return true;
  }

  /**
   * Will attempt to return device token or user’s authentication provider access token.
   * @param options - Token provider options for authentication
   * @returns Promise that resolves to the access token
   * @throws Error if the operation fails
   */
  @loggerDecorator.debug.d3()
  public static async getToken(
    options?: GetTokenOptions
  ): Promise<GetTokenAuthPluginError | AccessToken> {
    try {
      const authPlugin = await this.getAuthPlugin();
      if (!authPlugin) {
        throw new ClientOSError(
          'JWeb Auth plugin is not available',
          ClientOSErrorCodes.JWEB_AUTH_PLUGIN_UNAVAILABLE,
          new Error('JWeb error: Auth plugin is not available')
        );
      }

      // Only case of requesting a tenant exchange when the cache already have this one
      const initializationToken = InitializationTokenCache.getCachedToken();

      if (
        !!initializationToken &&
        !!initializationToken?.tenantId &&
        options?.tokenProviderOptions?.tenantID ===
          initializationToken?.tenantId
      ) {
        return initializationToken;
      }

      let sanitizedOptions = options;
      if (options?.tokenProviderOptions?.tokenLifetimeRequirements) {
        const appVersion = await getDeviceVersion();
        if (isHpSmartAppVersion(appVersion)) {
          /* istanbul ignore next */
          internalLogger.debug?.d4({
            className: 'AuthPluginFacade',
            methodName: 'getToken',
            message: 'Removing tokenLifetimeRequirements for HPSmart app',
            variables: {
              appVersion,
              originalOptions: options
            }
          });

          // Remove tokenLifetimeRequirements
          const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            tokenLifetimeRequirements,
            ...tokenProviderOptionsWithoutLifetime
          } = options.tokenProviderOptions;
          sanitizedOptions = {
            ...options,
            tokenProviderOptions: tokenProviderOptionsWithoutLifetime
          };
        }
      }

      //TODO: add the error handling here
      internalLogger.debug(
        'Calling getToken from JWeb Auth plugin (there is no initialization token cache) with options:',
        options
      );

      const jwebResult = await authPlugin.getToken(sanitizedOptions);
      const jwebError = (jwebResult as GetTokenAuthPluginError)?.error;

      if (jwebResult && jwebError) {
        if (jwebError?.currentAccessToken && jwebError?.currentIdToken) {
          // TODO: Commons should somehow warn consumers that token isn't fresh and might be expired
          AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE = true;
          return {
            tokenValue: jwebError?.currentAccessToken,
            account: {
              idToken: jwebError?.currentIdToken
            }
          } as unknown as AccessToken;
        }
        AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE = true;
        return jwebResult as GetTokenAuthPluginError;
      }

      AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE = false;
      return jwebResult;
    } catch (error) {
      AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE = true;

      const errorMessage = 'Failed to get authentication token';
      internalLogger.error(errorMessage, error);
      if (error instanceof ClientOSError) throw error;
      throw new ClientOSError(
        errorMessage,
        ClientOSErrorCodes.JWEB_AUTH_PLUGIN_RUNTIME_ERROR,
        error
      );
    }
  }

  /**
   * Logs out of an account and invalidates all of the account's tokens
   * stored in the native app and in the cloud.
   *
   * Rejects if there was an error.  Resolves with an empty object on success.
   * @returns Promise that resolves when logout is complete
   * @throws Error if the operation fails
   */
  @loggerDecorator.debug.d3()
  @timeout({ message: `JWeb: logout call timed out` })
  public static async logout(): Promise<GetTokenAuthPluginError | never> {
    try {
      const authPlugin = (await this.getAuthPlugin()) as AuthPlugin;
      const res = await authPlugin.logout();
      internalLogger.debug('JWeb: Successfully logged out user');
      return res;
    } catch (error) {
      const errorMessage = 'JWeb: Failed to logout user';
      internalLogger.error(errorMessage, error);
      throw new ClientOSError(
        errorMessage,
        ClientOSErrorCodes.JWEB_AUTH_PLUGIN_RUNTIME_ERROR,
        error
      );
    }
  }

  @loggerDecorator.debug.d3()
  @timeout({ message: `JWeb: getAuthenticationProvider call timed out` })
  public static async getAuthenticationProvider(): Promise<ProviderType> {
    try {
      const authPlugin = (await this.getAuthPlugin()) as AuthPlugin;
      const res = await authPlugin.getAuthenticationProvider();
      return (
        res as {
          value: ProviderType;
        }
      )?.value;
    } catch (error) {
      const errorMessage = 'JWeb: Failed to get Authentication Provider.';
      internalLogger.error(errorMessage, error);
    }
  }

  /**
   * Checks the current logged in status of the user.
   * JWeb info: This call only checks the status using information contained locally on  the device. It does not cause any network calls nor does it show user interaction.
   * @returns Promise that resolves to true if user is logged in, false otherwise
   */
  public static async isLoggedIn(): Promise<
    | GetTokenAuthPluginError
    | {
        value: boolean;
      }
  > {
    try {
      const authPlugin = await this.getAuthPlugin();
      const loggedIn = await withTimeout(authPlugin.isLoggedIn(), {
        message: 'JWeb: isLoggedIn call timed out'
      });

      internalLogger.debug(`JWeb: User login status:`, loggedIn);
      return loggedIn;
    } catch (error) {
      const errorMessage = 'JWeb error: Failed to check user login status';
      internalLogger.error(errorMessage, error);
      throw new ClientOSError(
        errorMessage,
        ClientOSErrorCodes.JWEB_AUTH_PLUGIN_RUNTIME_ERROR,
        error
      );
    }
  }

  public static async isIdentityProviderLookupEnabled(): Promise<{
    value: boolean;
  }> {
    try {
      const authPlugin = await this.getAuthPlugin();
      if (!authPlugin) {
        return { value: false };
      }
      return await authPlugin.isIdentityProviderLookupEnabled();
    } catch (error) {
      internalLogger.error(
        'JWeb: Failed to check identity provider lookup status',
        error
      );
      return { value: false };
    }
  }

  public static async getIdentityProviderConfigurations(options: {
    email: string;
    providerName?: string;
  }): Promise<IdentityProviderConfigurationsResult> {
    try {
      const authPlugin = await this.getAuthPlugin();
      if (!authPlugin) {
        throw new ClientOSError(
          'JWeb Auth plugin is not available',
          ClientOSErrorCodes.JWEB_AUTH_PLUGIN_UNAVAILABLE,
          new Error('JWeb error: Auth plugin is not available')
        );
      }

      // TODO: Remove this as any
      const result = await authPlugin.getIdentityProviderConfigurations(
        options
      );
      return result;
    } catch (error) {
      const errorMessage =
        'JWeb error: Failed to get identity provider configurations';
      internalLogger.error(errorMessage, error);
      if (error instanceof ClientOSError) throw error;
      throw new ClientOSError(
        errorMessage,
        ClientOSErrorCodes.JWEB_AUTH_PLUGIN_RUNTIME_ERROR,
        error
      );
    }
  }
}
